import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";

import { ProctoringContext } from "contexts";
import useAssessmentStore from "components/Assessment/assessmentStore";
import { AssessmentStates } from "components/Assessment/constants";

/**
 * Camera preview (mobile) — mobile equivalent of `Assessment/VideoPreview`.
 *
 * Same job as desktop: hold the camera stream that image-capture proctoring
 * samples from, and let the candidate toggle it off (which desktop records as a
 * CAMERA_DISABLED violation via `onCameraDisable`). Laid out per the Figma: a
 * wide banner with the toggle centred over the feed.
 *
 * `facingMode: "user"` is the one real mobile addition — phones default to the
 * rear camera otherwise.
 */
export default function VideoPreview() {
  const { activateImageProctoring, deactivateImageProctoring, onCameraDisable } =
    React.useContext(ProctoringContext);

  const assessmentState = useAssessmentStore((state) => state.assessmentState);
  const template = useAssessmentStore((state) => state.template);

  const [enabled, setEnabled] = React.useState(true);
  const [loading, setLoading] = React.useState(false);

  const streamRef = React.useRef(null);
  const videoRef = React.useRef(null);

  const showComponent = [
    AssessmentStates.SHOWING_TUTORIAL,
    AssessmentStates.RUNNING,
  ].includes(assessmentState);

  const toggleProctoring = (videoEnabled) => {
    if (template && assessmentState === AssessmentStates.RUNNING) {
      if (videoEnabled) {
        const { proctorSettings = {} } = template;
        if (proctorSettings?.imageCaptureProctoring) {
          activateImageProctoring(120000, streamRef, videoRef).catch(
            console.error
          );
        }
      } else {
        deactivateImageProctoring(true);
      }
    }
  };

  React.useEffect(() => {
    if (!enabled) onCameraDisable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  React.useEffect(() => {
    (async () => {
      if (showComponent && enabled) {
        setLoading(true);
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: { facingMode: "user" },
          });
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play();
          } else {
            streamRef.current?.getTracks()?.forEach((track) => track.stop());
            setLoading(false);
          }
        } catch (error) {
          console.error(error);
          setEnabled(false);
          setLoading(false);
        }
      } else {
        streamRef.current?.getTracks()?.forEach((track) => track.stop());
        setLoading(false);
      }
      toggleProctoring(enabled);
    })();

    return () => {
      streamRef.current?.getTracks()?.forEach((track) => track.stop());
      setLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, showComponent]);

  return (
    <Box
      sx={{
        flexShrink: 0,
        position: "relative",
        width: "100%",
        height: 190,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "#353535",
      }}
    >
      <Box
        component="video"
        ref={videoRef}
        playsInline
        muted
        onPlay={() => setLoading(false)}
        sx={{
          display: enabled && !loading ? "block" : "none",
          width: "100%",
          height: "100%",
          objectFit: "cover",
          // Mirror the feed so it reads like a selfie view, as desktop does.
          transform: "rotateY(180deg)",
        }}
      />

      {enabled && loading && (
        <Box sx={sxCentered}>
          <Typography variant="body02-regular" color="#CACACA">
            Buffering...
          </Typography>
        </Box>
      )}

      {!enabled && (
        <Box sx={sxCentered}>
          <Typography variant="body02-regular" color="#CACACA">
            Tap to turn on your camera
          </Typography>
        </Box>
      )}

      {/* Centre toggle */}
      <IconButton
        onClick={() => setEnabled((e) => !e)}
        aria-label={enabled ? "Turn camera off" : "Turn camera on"}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "shades.clr-black-900",
          color: "shades.clr-white-900",
          "&:hover": { bgcolor: "shades.clr-black-900" },
        }}
      >
        {enabled ? <VideocamIcon /> : <VideocamOffIcon />}
      </IconButton>
    </Box>
  );
}

const sxCentered = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
